import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch and handle React errors gracefully
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Or with custom fallback:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // You could also send to an error reporting service here
    // logger.error('React Error Boundary', { error, errorInfo });

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="container mx-auto max-w-3xl py-8">
          <Card className="border border-border">
            <CardContent className="p-6 text-center">
              <div className="mb-4">
                <AlertTriangle
                  size={64}
                  className="text-destructive mx-auto"
                  strokeWidth={1.5}
                />
              </div>

              <h1 className="text-3xl font-semibold mb-3">
                Quelque chose s'est mal passé
              </h1>

              <p className="text-muted-foreground mb-4">
                Une erreur inattendue s'est produite. Nos équipes ont été notifiées.
              </p>

              {process.env.NODE_ENV === 'development' && error && (
                <Card className="mb-4 bg-destructive/10 border-destructive/40 max-h-[200px] overflow-auto text-left">
                  <CardContent className="p-3">
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs">
                      {error.toString()}
                      {error.stack && `\n\n${error.stack}`}
                    </pre>
                  </CardContent>
                </Card>
              )}

              <div className="flex flex-wrap gap-2 justify-center">
                <Button variant="outline" onClick={this.handleReset}>
                  <RefreshCw size={18} className="mr-2" />
                  Réessayer
                </Button>

                <Button variant="outline" onClick={this.handleReload}>
                  <RefreshCw size={18} className="mr-2" />
                  Recharger la page
                </Button>

                <Button onClick={this.handleGoHome}>
                  <Home size={18} className="mr-2" />
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
